import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WiziQuizHeader from '../components/wizi-quiz/WiziQuizHeader';
import WiziQuizNavigator from '../components/wizi-quiz/WiziQuizNavigator';
import WiziQuizActionButtons from '../components/wizi-quiz/WiziQuizActionButtons';
import {
  getWiziQuizAttemptQuestions,
  submitWiziQuizAnswer,
  updateWiziQuizQuestionStatus,
  updateCurrentQuestion,
  completeWiziQuizAttempt,
  isWiziQuizSuccess,
  getWiziQuizErrorMessage,
} from '../utils/wiziQuizApiClient';
import {
  WiziQuizSessionState,
  WiziQuestionState,
  saveWiziQuizState,
  loadWiziQuizState,
  clearWiziQuizState,
  saveWiziQuizStateThrottled,
} from '../utils/wiziQuizStateManager';
import { getUserSession } from '../components/apiClient';

interface Question {
  id: number;
  wizi_question_id: number;
  question_order: number;
  marks: number;
  negative_marks: number;
  user_answer: string | null;
  status: string;
  time_spent: number;
  question_img_url: string;
  question_text: string;
  option_count: number;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  subject_id: number;
  chapter_name: string;
  question_type: 'mcq' | 'integer';
}

interface AttemptInfo {
  id: number;
  wizi_quiz_id: number;
  attempt_number: number;
  attempt_status: string;
  current_question_index: number;
  total_questions: number;
  answered_questions: number;
  started_at: string;
  time_spent: number;
}

interface QuizData {
  id: number;
  name: string;
  description: string;
  time_limit: number; // in minutes
  total_marks: number;
  level: string;
}

const WiziQuizPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();

  // Data state
  const [quizData, setQuizData] = useState<QuizData | null>(null);
  const [attemptInfo, setAttemptInfo] = useState<AttemptInfo | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Quiz state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionStates, setQuestionStates] = useState<WiziQuestionState[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(0); // in seconds
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // UI state
  const [saving, setSaving] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  const userSession = getUserSession();

  // Get current quiz state for saving
  const getCurrentState = useCallback((): WiziQuizSessionState | null => {
    if (!attemptInfo) return null;

    return {
      attempt_id: attemptInfo.id,
      wizi_quiz_id: attemptInfo.wizi_quiz_id,
      current_question_index: currentQuestionIndex,
      time_remaining: timeRemaining,
      question_states: questionStates,
      last_saved: Date.now(),
      total_time_limit: quizData?.time_limit ? quizData.time_limit * 60 : 0,
    };
  }, [attemptInfo, currentQuestionIndex, timeRemaining, questionStates, quizData]);

  // Load quiz data
  useEffect(() => {
    const loadQuizData = async () => {
      if (!attemptId) {
        setError('Attempt ID not provided');
        setLoading(false);
        return;
      }

      const numericAttemptId = parseInt(attemptId);

      // Try to load saved state first
      const savedState = loadWiziQuizState(numericAttemptId);
      if (savedState) {
        console.log('Restoring saved quiz state...');
        setCurrentQuestionIndex(savedState.current_question_index);
        setTimeRemaining(savedState.time_remaining);
        setQuestionStates(savedState.question_states);
        // We still need to fetch questions from API
      }

      try {
        const response = await getWiziQuizAttemptQuestions(numericAttemptId);

        if (!isWiziQuizSuccess(response)) {
          throw new Error(getWiziQuizErrorMessage(response));
        }

        const { attempt, questions: fetchedQuestions } = response.result;
        setAttemptInfo(attempt);
        setQuestions(fetchedQuestions);

        // Initialize question states if not loaded from session
        if (!savedState) {
          const initialStates: WiziQuestionState[] = fetchedQuestions.map((q: Question) => ({
            status: (q.status as any) || 'not_attempted',
            user_answer: q.user_answer,
            time_spent: q.time_spent || 0,
            visited: false,
          }));
          setQuestionStates(initialStates);

          // Calculate initial time
          // TODO: Get quiz details for time_limit
          const totalTime = 180 * 60; // Default 180 minutes
          setTimeRemaining(totalTime - (attempt.time_spent || 0));
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading quiz:', err);
        setError(err instanceof Error ? err.message : 'Failed to load quiz');
        setLoading(false);
      }
    };

    loadQuizData();
  }, [attemptId]);

  // Save state periodically
  useEffect(() => {
    if (attemptInfo) {
      const state = getCurrentState();
      if (state) {
        saveWiziQuizStateThrottled(state);
      }
    }
  }, [currentQuestionIndex, questionStates, timeRemaining, attemptInfo, getCurrentState]);

  // Timer
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const deltaSeconds = Math.floor((now - lastUpdateTime) / 1000);

      if (deltaSeconds >= 1 && deltaSeconds <= 10) {
        setLastUpdateTime(now);
        setTimeRemaining((prev) => {
          const newTime = Math.max(0, prev - deltaSeconds);
          if (newTime === 0) {
            handleTimeUp();
          }
          return newTime;
        });

        // Update time spent for current question
        setQuestionStates((prev) => {
          const updated = [...prev];
          if (updated[currentQuestionIndex]) {
            updated[currentQuestionIndex] = {
              ...updated[currentQuestionIndex],
              time_spent: updated[currentQuestionIndex].time_spent + deltaSeconds,
            };
          }
          return updated;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, lastUpdateTime, currentQuestionIndex]);

  // Mark current question as visited
  useEffect(() => {
    if (questions.length > 0 && questionStates[currentQuestionIndex]) {
      setQuestionStates((prev) => {
        const updated = [...prev];
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          visited: true,
        };
        return updated;
      });
    }
  }, [currentQuestionIndex, questions.length]);

  // Handle time up
  const handleTimeUp = useCallback(async () => {
    if (attemptInfo) {
      await completeWiziQuizAttempt(attemptInfo.id);
      clearWiziQuizState();
      navigate(`/mock-test/result/${attemptInfo.id}`);
    }
  }, [attemptInfo, navigate]);

  // Handle answer selection
  const handleAnswerSelect = (answer: string) => {
    // For numerical questions, validate that it's a valid number
    if (questions[currentQuestionIndex]?.question_type === 'integer') {
      // Allow empty string (clearing answer) or valid numbers (including decimals and negative)
      if (answer !== '' && !/^-?\d*\.?\d*$/.test(answer)) {
        return; // Ignore invalid input
      }
    }

    setQuestionStates((prev) => {
      const updated = [...prev];
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        user_answer: answer,
        status: answer ? 'answered' : 'not_attempted',
      };
      return updated;
    });
  };

  // Save answer to API
  const saveAnswer = async (status: 'answered' | 'answered_marked_review') => {
    const currentState = questionStates[currentQuestionIndex];
    const currentQuestion = questions[currentQuestionIndex];

    if (!currentState.user_answer) {
      alert('Please select an answer first');
      return false;
    }

    setSaving(true);
    try {
      const response = await submitWiziQuizAnswer({
        question_user_id: currentQuestion.id,
        user_answer: currentState.user_answer,
        time_spent: currentState.time_spent,
      });

      if (!isWiziQuizSuccess(response)) {
        throw new Error(getWiziQuizErrorMessage(response));
      }

      return true;
    } catch (err) {
      console.error('Error saving answer:', err);
      alert('Failed to save answer. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Action handlers
  const handleSaveAndNext = async () => {
    const saved = await saveAnswer('answered');
    if (saved && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      if (attemptInfo) {
        await updateCurrentQuestion({
          attempt_id: parseInt(attemptId!),
          current_question_index: currentQuestionIndex + 1
        });
      }
    }
  };

  const handleClear = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    setSaving(true);
    try {
      const response = await updateWiziQuizQuestionStatus({
        question_user_id: currentQuestion.id,
        status: 'not_attempted',
      });

      if (isWiziQuizSuccess(response)) {
        setQuestionStates((prev) => {
          const updated = [...prev];
          updated[currentQuestionIndex] = {
            ...updated[currentQuestionIndex],
            user_answer: null,
            status: 'not_attempted',
          };
          return updated;
        });
      }
    } catch (err) {
      console.error('Error clearing answer:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndMarkForReview = async () => {
    await saveAnswer('answered_marked_review');
  };

  const handleMarkForReviewAndNext = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    const currentState = questionStates[currentQuestionIndex];

    setSaving(true);
    try {
      const status = currentState.user_answer ? 'answered_marked_review' : 'marked_review';
      
      if (currentState.user_answer) {
        await submitWiziQuizAnswer({
          question_user_id: currentQuestion.id,
          user_answer: currentState.user_answer,
          time_spent: currentState.time_spent,
        });
      } else {
        await updateWiziQuizQuestionStatus({
          question_user_id: currentQuestion.id,
          status: 'marked_review',
        });
      }

      setQuestionStates((prev) => {
        const updated = [...prev];
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          status,
        };
        return updated;
      });

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
        if (attemptInfo) {
          await updateCurrentQuestion({
            attempt_id: parseInt(attemptId!),
            current_question_index: currentQuestionIndex + 1
          });
        }
      }
    } catch (err) {
      console.error('Error marking for review:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = async () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      if (attemptInfo) {
        await updateCurrentQuestion({
          attempt_id: parseInt(attemptId!),
          current_question_index: currentQuestionIndex - 1
        });
      }
    }
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      if (attemptInfo) {
        await updateCurrentQuestion({
          attempt_id: parseInt(attemptId!),
          current_question_index: currentQuestionIndex + 1
        });
      }
    }
  };

  const handleSubmit = () => {
    setShowSubmitDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowSubmitDialog(false);

    if (attemptInfo) {
      try {
        await completeWiziQuizAttempt(attemptInfo.id);
        clearWiziQuizState();
        navigate(`/mock-test/result/${attemptInfo.id}`);
      } catch (err) {
        console.error('Error submitting quiz:', err);
        alert('Failed to submit quiz. Please try again.');
      }
    }
  };

  const handleQuestionSelect = async (index: number) => {
    setCurrentQuestionIndex(index);
    if (attemptInfo) {
      await updateCurrentQuestion({
        attempt_id: parseInt(attemptId!),
        current_question_index: index
      });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading test...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Error Loading Test</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/mock-test')}>
            Back to Test List
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentState = questionStates[currentQuestionIndex];

  if (!currentQuestion || !currentState) {
    return <div>Loading question...</div>;
  }

  return (
    <div className="vh-100 d-flex flex-column">
      {/* Header */}
      <WiziQuizHeader
        candidateName={userSession?.display_name || userSession?.username || 'Student'}
        examName={attemptInfo?.wizi_quiz_id ? `Mock Test #${attemptInfo.wizi_quiz_id}` : 'Test'}
        subjectName="Multi-Subject"
        timeRemaining={timeRemaining}
      />

      {/* Main Content */}
      <div className="flex-grow-1 d-flex overflow-hidden">
        {/* Question Area */}
        <div className="flex-grow-1 overflow-auto p-4" style={{ flex: '0 0 70%' }}>
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Question {currentQuestionIndex + 1}:</h5>
            </div>
            <div className="card-body">
              {/* Question Image/Text */}
              {currentQuestion.question_img_url && (
                <img
                  src={currentQuestion.question_img_url}
                  alt="Question"
                  className="img-fluid mb-3"
                  style={{ maxHeight: '400px', objectFit: 'contain' }}
                />
              )}
              {currentQuestion.question_text && (
                <div className="mb-3">
                  <p className="fs-5">{currentQuestion.question_text}</p>
                </div>
              )}

              {/* Options - Conditional rendering based on question_type */}
              <div className="mt-4">
                {currentQuestion.question_type === 'mcq' ? (
                  // MCQ - Radio buttons
                  <div className="row g-2">
                    {['A', 'B', 'C', 'D'].slice(0, currentQuestion.option_count).map((option, idx) => {
                      const optionKey = `option_${option.toLowerCase()}` as keyof Question;
                      const optionText = currentQuestion[optionKey] as string;
                      const optionValue = (idx + 1).toString();
                      const isSelected = currentState.user_answer === optionValue;

                      return (
                        <div key={option} className="col-12 col-md-6">
                          <div
                            className={`form-check p-3 border rounded h-100 ${
                              isSelected ? 'bg-primary bg-opacity-10 border-primary' : ''
                            }`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => !saving && handleAnswerSelect(optionValue)}
                          >
                            <input
                              className="form-check-input"
                              type="radio"
                              name="answer"
                              id={`option${option}`}
                              checked={isSelected}
                              onChange={() => handleAnswerSelect(optionValue)}
                              disabled={saving}
                            />
                            <label
                              className="form-check-label w-100"
                              htmlFor={`option${option}`}
                              style={{ cursor: 'pointer' }}
                            >
                              <strong>{option}.</strong> {optionText}
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Numerical - Number input
                  <div className="form-group">
                    <label htmlFor="numericalAnswer" className="form-label fw-bold mb-3">
                      Enter your answer (numerical value):
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="numericalAnswer"
                      placeholder="Enter your answer"
                      value={currentState.user_answer || ''}
                      onChange={(e) => handleAnswerSelect(e.target.value)}
                      disabled={saving}
                      style={{ 
                        fontSize: '1.2rem', 
                        padding: '12px',
                        maxWidth: '300px'
                      }}
                    />
                    <small className="form-text text-muted mt-2 d-block">
                      Enter numerical value (integers and decimals allowed)
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigator */}
        <div style={{ flex: '0 0 30%', minWidth: '300px' }}>
          <WiziQuizNavigator
            questions={questions}
            questionStates={questionStates}
            currentQuestionIndex={currentQuestionIndex}
            onQuestionSelect={handleQuestionSelect}
            onSubmit={handleSubmit}
            disabled={saving}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <WiziQuizActionButtons
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={questions.length}
        hasAnswer={!!currentState.user_answer}
        isMarkedForReview={currentState.status.includes('marked')}
        onSaveAndNext={handleSaveAndNext}
        onClear={handleClear}
        onSaveAndMarkForReview={handleSaveAndMarkForReview}
        onMarkForReviewAndNext={handleMarkForReviewAndNext}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSubmit={handleSubmit}
        disabled={saving}
      />

      {/* Submit Confirmation Dialog */}
      {showSubmitDialog && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Submission</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSubmitDialog(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to submit the quiz?</p>
                <p className="text-muted">
                  Answered: {questionStates.filter((s) => s.user_answer).length}/{questions.length}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSubmitDialog(false)}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmSubmit}>
                  Submit Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WiziQuizPage;
